import React, { useContext, useRef, useState } from 'react';
import { Accordion } from 'components/Accordion';
import { JumpLinkList } from 'components/JumpLinkList';
import { ExpandableJumpLinkList } from 'components/ExpandableJumpLinkList';
import { useActiveSection } from 'lib/Hooks/useActiveSection';
import { VerticalContext } from 'lib/ContextTypes';
import classNames from 'classnames';
import shared from 'styles/shared.module.scss';
import styles from './styles.module.scss';

/**
 * @typedef {object} LinkItem
 * @property {string} linkText - The text to display for the link
 * @property {string} sectionId - The ID of the section this link points to
 */

/**
 * Renders a list of jump links to content within the page.
 *
 * @param {object} props
 * @param {boolean} [props.isCollapsible=true]
 * @param {LinkItem[]} [props.links] - Array of link objects for navigation
 * @param {string} [props.id="table-of-contents"]
 * @param {string} [props.testId="table-of-contents"]
 * @param {string} [props.title=""]
 * @param {boolean} [props.visible=true]
 * @param {string} [props.lastInteraction]
 * @param {"default"|"inline"|"sidebar"|"topnav"} [props.variant="default"]
 * @returns {React.ReactElement|null} - The rendered component or null.
 */
const TableOfContents = ({
  id = 'table-of-contents',
  isCollapsible = true,
  lastInteraction = undefined,
  links = [],
  testId = 'table-of-contents',
  title = '',
  visible = true,
  variant = 'default',
}) => {
  const componentRef = useRef(null);
  const [isOpen, setIsOpen] = useState(!isCollapsible);
  const [wasFocusedViaKeyboard, setWasFocusedViaKeyboard] = useState(false);

  const sectionIds = links.map((link) => link.href);
  const activeSection = useActiveSection(sectionIds);

  const vertical = useContext(VerticalContext);
  const isTodayVertical = vertical === 'today';

  const isInlineVariant = variant === 'inline';
  const isTopnavVariant = variant === 'topnav';

  /**
   * Toggles the accordion open and close.
   */
  const toggleAccordion = () => {
    if (isCollapsible) {
      setIsOpen((prev) => !prev);
    }
  };

  /**
   * Prevents the table of contents from hiding after it has been focused via keyboard.
   */
  const onAccordionFocus = () => {
    if (lastInteraction === 'keydown') {
      setWasFocusedViaKeyboard(true);
    }
  };

  if (!title || !links.length) {
    return null;
  }

  return (
    <div
      className={classNames(
        styles.tableOfContents,
        { [styles.hidden]: !visible && !wasFocusedViaKeyboard },
        { [styles.fullBleed]: isInlineVariant },
        { [shared.parallelogram]: isTodayVertical && !isTopnavVariant },
        {
          [shared.parallelogramNoAffordance]: isTodayVertical
          && !isTopnavVariant
          && !isInlineVariant,
        },
      )}
      data-testid={testId}
      data-variant={variant}
      data-collapsible={isCollapsible}
      ref={componentRef}
      role="button"
      tabIndex="-1"
      id={id}
    >
      <Accordion
        additionalStyles={{
          accordion: isInlineVariant && styles.tableOfContents__accordionFullBleed,
          open: styles.tableOfContents__open,
          accordion__content: isInlineVariant
            ? styles.tableOfContents__accordionContentFullBleed
            : styles.tableOfContents__accordion,
          accordion__header: styles.tableOfContents__accordionHeader,
        }}
        isCollapsible={isCollapsible}
        showAffordanceWithCount={isInlineVariant}
        isOpen={isOpen}
        onToggle={toggleAccordion}
        onFocus={onAccordionFocus}
        title={title}
      >
        { !isInlineVariant && (
          <JumpLinkList
            callback={toggleAccordion}
            clickTrackingId="mbt_tableofcontents_jumplink"
            activeLink={activeSection}
            links={links}
          />
        )}

        { isInlineVariant && (
          <ExpandableJumpLinkList
            callback={toggleAccordion}
            clickTrackingId="mbt_tableofcontents_jumplink"
            links={links}
          />
        )}
      </Accordion>
    </div>
  );
};

export {
  TableOfContents,
};
